// EventDetailModal.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Button,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import ImageIcon from "@mui/icons-material/Image";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DevicesIcon from "@mui/icons-material/Devices";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { getAlarmDetail, getAlarmPoi, getAlarmGpsByDeviceId } from "../utility";
import CommonMap from "./commanMapView";

const ALARM_TYPE_MAP = {
  forwardCollisionWarning: "Forward collision",
  laneChangeWarning: "Lane Departure",
  driverFatigue: "Driver Fatigue",
  overspeed: "Overspeed",
  geofence: "Geofence",
  routeDeviation: "Route Deviation",
  tampering: "Tampering",
};
const toDisplayType = (raw) => ALARM_TYPE_MAP[raw] ?? raw ?? "—";

// ─── Thumbnail component ──────────────────────────────────────────────────────
// Renders at 2x pixel density and crisp object-fit so small previews don't
// look soft/blurry, with a clear play badge for video channels.
const ThumbItem = ({ file, isActive, onClick, label }) => {
  const [imgErr, setImgErr] = useState(false);
  const isVideo = file?.fileType === "02";

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
       width: 82,
height: 88,
        borderRadius: "8px",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        flexShrink: 0,
        background: "#111827",
        outline: isActive ? "2px solid #5b8def" : "2px solid transparent",
        outlineOffset: "1px",
        boxShadow: isActive
          ? "0 4px 12px rgba(91,141,239,0.35)"
          : "0 1px 2px rgba(15,23,42,0.06)",
        transition: "outline-color .15s, transform .15s, box-shadow .15s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 14px rgba(15,23,42,0.18)",
        },
      }}
    >
      {!file ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(145deg, #f3f4f6 0%, #e9ecf1 100%)",
            gap: "3px",
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 17, color: "#aab3c2" }} />
          <Typography
            sx={{ fontSize: "9px", color: "#9aa4b2", fontWeight: 600 }}
          >
            {label}
          </Typography>
        </Box>
      ) : (
        <>
          {isVideo ? (
            <video
              src={file.fileUrl}
              muted
              playsInline
              preload="metadata"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : imgErr ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f3f4f6",
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 17, color: "#aab3c2" }} />
            </Box>
          ) : (
            <Box
              component="img"
              src={file.fileUrl}
              alt={label}
              loading="lazy"
              onError={() => setImgErr(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                imageRendering: "auto",
                display: "block",
              }}
            />
          )}

          {isVideo && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)",
              }}
            >
              <PlayCircleIcon
                sx={{ fontSize: 26, color: "rgba(255,255,255,0.92)" }}
              />
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              px: "6px",
              py: "3px",
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              color: "#fff",
              background:
                "linear-gradient(0deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 100%)",
            }}
          >
            {label}
            {isVideo ? " · VID" : ""}
          </Box>
        </>
      )}
    </Box>
  );
};

// ─── Main viewer (center panel) ───────────────────────────────────────────────
// Dark canvas + soft vignette make both stills and video read sharper than
// a flat light-gray backdrop, which is what was washing out contrast before.
const MainViewer = ({ file }) => {
  const [imgErr, setImgErr] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const isVideo = file?.fileType === "02";

  useEffect(() => {
    setLoaded(false);
    setImgErr(false);
  }, [file?.fileUrl]);

  const canvasSx = {
    width: "100%",
    height: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
 background:
  "radial-gradient(circle at 50% 40%, #1b2333 0%, #0c111d 100%)",
  };

  if (!file) {
    return (
      <Box
        sx={{
          ...canvasSx,
          background: "#f6f7f9",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(91,141,239,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ImageIcon sx={{ fontSize: 30, color: "#9aa9c4" }} />
        </Box>
        <Typography sx={{ color: "#8b95a6", fontSize: "13px", fontWeight: 500 }}>
          No media available for this channel
        </Typography>
      </Box>
    );
  }

  if (isVideo) {
    return (
      <Box sx={canvasSx}>
        <video
          key={file.fileUrl}
          src={file.fileUrl}
          controls
          autoPlay
          muted
          playsInline
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            filter: "contrast(1.04) saturate(1.05)",
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={canvasSx}>
      {imgErr ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 44, color: "#5b6679" }} />
          <Typography sx={{ color: "#9aa4b2", fontSize: "12px" }}>
            Failed to load image
          </Typography>
        </Box>
      ) : (
        <>
          {!loaded && (
            <CircularProgress
              size={28}
              sx={{ color: "#5b8def", position: "absolute" }}
            />
          )}
          <Box
            component="img"
            src={file.fileUrl}
            alt="Event media"
            onLoad={() => setLoaded(true)}
            onError={() => setImgErr(true)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: loaded ? 1 : 0,
              transition: "opacity .25s ease",
              // sharpen perceived clarity slightly without over-processing
              filter: "contrast(1.05) saturate(1.06)",
              imageRendering: "auto",
            }}
          />
          
        </>
      )}
    </Box>
  );
};

// ─── Sidebar event list item ──────────────────────────────────────────────────
const SidebarItem = ({ record, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: "14px",
      py: "11px",
      cursor: "pointer",
      position: "relative",
      background: isActive ? "rgba(91,141,239,0.07)" : "transparent",
      transition: "background .15s",
      "&:hover": {
        background: isActive ? "rgba(91,141,239,0.1)" : "rgba(15,23,42,0.035)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "3px",
        background: isActive ? "#5b8def" : "transparent",
        borderRadius: "0 3px 3px 0",
        transition: "background .15s",
      },
    }}
  >
    <Typography
      sx={{
        fontSize: "13px",
        fontWeight: isActive ? 600 : 500,
        color: isActive ? "#0f172a" : "#475065",
        lineHeight: 1.3,
      }}
    >
      {record.deviceName}
    </Typography>
    <Typography
      sx={{
        fontSize: "11px",
        color: isActive ? "#3f63b8" : "#71879f",
        mt: "3px",
        fontWeight: isActive ? 600 : 400,
      }}
    >
      {toDisplayType(record.alarmType)}
    </Typography>
    <Typography sx={{ fontSize: "10px", color: "#9aa4b2", mt: "2px" }}>
      {record.alarmTime}
    </Typography>
  </Box>
);

// ─── Channel labels ───────────────────────────────────────────────────────────
const CH_LABELS = ["CH1", "CH2", "CH3", "CH4"];

function buildCamSlots(fileList = []) {
  const slots = [null, null, null, null];
  for (const f of fileList) {
    const m = f.fileUrl?.match(/_0(\d)_0[02]_/);
    if (m) {
      const idx = parseInt(m[1], 10);
      if (idx >= 0 && idx <= 3) {
        if (
          !slots[idx] ||
          (slots[idx].fileType !== "01" && f.fileType === "01")
        ) {
          slots[idx] = f;
        }
      }
    }
  }
  if (slots.every((s) => s === null) && fileList.length > 0) {
    fileList.slice(0, 4).forEach((f, i) => {
      slots[i] = f;
    });
  }
  return slots;
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
const EventDetailModal = ({
  open,
  onClose,
  records,
  fileMap,
  initialRecord,
}) => {
  const [gpsPoints, setGpsPoints] = useState([]);
  const [activeRecord, setActiveRecord] = useState(initialRecord);
  const [activeFileIdx, setActiveFileIdx] = useState(0);
  const [detail, setDetail] = useState(null);
  const [poi, setPoi] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const mapRef = useRef(null);

  const routePath = gpsPoints.map((item) => ({
    lat: item.lat,
    lng: item.lon,
  }));

  const onMapLoad = (map) => {
    mapRef.current = map;
    if (routePath.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      routePath.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (initialRecord) {
      setActiveRecord(initialRecord);
      setActiveFileIdx(0);
    }
  }, [initialRecord, open]);

  useEffect(() => {
    if (!activeRecord || !open) return;

    const fetchDetail = async () => {
      setLoadingDetail(true);
      setDetailError(null);
      setDetail(null);
      setPoi(null);

      try {
        const detailRes = await getAlarmDetail(activeRecord.alarmId);
        if (detailRes.code === 0 && detailRes.data) {
          setDetail(detailRes.data);

          const { lat, lon } = detailRes.data;
          if (lat && lon) {
            try {
              const poiRes = await getAlarmPoi({ lat, lon });
              if (poiRes.code === 0) setPoi(poiRes.data);
            } catch (_) {}

            try {
              const alarmTime = activeRecord.alarmTime;
              const startDate = new Date(alarmTime.replace(" ", "T"));
              const endDate = new Date(startDate.getTime() + 60 * 1000);

              const formatUTC = (date) => {
                const pad = (n) => String(n).padStart(2, "0");
                return `${date.getUTCFullYear()}-${pad(
                  date.getUTCMonth() + 1,
                )}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(
                  date.getUTCMinutes(),
                )}:${pad(date.getUTCSeconds())}`;
              };

              const start = formatUTC(startDate);
              const end = formatUTC(endDate);
              const gpsRes = await getAlarmGpsByDeviceId({
                deviceId: activeRecord.deviceId,
                start,
                end,
              });

              if (gpsRes.code === 0) {
                setGpsPoints(gpsRes.data || []);
              }
            } catch (_) {}
          }
        }
      } catch (err) {
        setDetailError(err.message ?? "Failed to load event detail");
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [activeRecord?.alarmId, open]);

  const currentFiles = activeRecord
    ? (fileMap[activeRecord.alarmId] ?? [])
    : [];
  const camSlots = buildCamSlots(currentFiles);
  const activeFile =
    camSlots[activeFileIdx] ?? currentFiles[activeFileIdx] ?? null;

  const lat = detail?.lat;
  const lon = detail?.lon;
  const address = poi?.displayName ?? detail?.address ?? "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "calc(100vw - 48px)",
          maxWidth: 1480,
          height: "calc(100vh - 48px)",
          maxHeight: 860,
          borderRadius: "14px",
          overflow: "hidden",
          background: "#ffffff",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 24px 60px rgba(15,23,42,0.28), 0 4px 14px rgba(15,23,42,0.12)",
        },
      }}
    >
      {/* ── Title bar ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid rgba(15,23,42,0.07)",
          background:
            "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#5b8def",
              boxShadow: "0 0 0 4px rgba(91,141,239,0.14)",
            }}
          />
          <Typography
            sx={{ fontSize: "15px", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01px" }}
          >
            Event Detail
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "rgba(15,23,42,0.55)",
            "&:hover": { color: "#0f172a", background: "rgba(15,23,42,0.05)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left sidebar – event list */}
        <Box
          sx={{
            width: 178,
            flexShrink: 0,
            borderRight: "1px solid rgba(15,23,42,0.06)",
            overflowY: "auto",
            background: "#fcfcfd",
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(15,23,42,0.08)",
              borderRadius: 2,
            },
          }}
        >
          {records.map((rec) => (
            <React.Fragment key={rec.alarmId}>
              <SidebarItem
                record={rec}
                isActive={rec.alarmId === activeRecord?.alarmId}
                onClick={() => {
                  setActiveRecord(rec);
                  setActiveFileIdx(0);
                }}
              />
              <Divider sx={{ borderColor: "rgba(15,23,42,0.045)" }} />
            </React.Fragment>
          ))}
        </Box>

        {/* Center – media viewer + thumbnails */}
       <Box
  sx={{
    flex: "0 0 auto",
    width: "calc(55% - 178px)",
    minWidth: 380,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid rgba(15,23,42,0.06)",
    background: "#f5f5f5", // whitesmoke
  }}
>
          {/* Main viewer */}
       {/* Event Header */}
{/* Event Header */}
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
    px: 2.5,
    py: 1.5,
    borderBottom: "1px solid rgba(15,23,42,0.07)",
    background:
      "linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)",
    flexShrink: 0,
  }}
>
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: "#5b8def",
      boxShadow: "0 0 0 4px rgba(91,141,239,0.14)",
    }}
  />

  <Typography
    sx={{
      fontSize: "15px",
      fontWeight: 700,
      color: "#0f172a",
      letterSpacing: "-0.01px",
    }}
  >
    {toDisplayType(activeRecord?.alarmType)}
  </Typography>
</Box>

{/* Main viewer */}
<Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
  <MainViewer file={activeFile} />
</Box>

          {/* Thumbnail strip */}
          <Box
            sx={{
              flexShrink: 0,
              background: "#15192a",
              borderTop: "1px solid rgba(255,255,255,0.06)",
            p: "6px 10px",
              display: "flex",
              gap: "10px",
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 3 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(255,255,255,0.12)",
              },
            }}
          >
            {camSlots.map((file, i) => (
              <ThumbItem
                key={i}
                file={file}
                label={CH_LABELS[i]}
                isActive={activeFileIdx === i}
                onClick={() => setActiveFileIdx(i)}
              />
            ))}
          </Box>
        </Box>

        {/* Right – map + detail */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Map */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              background: "#ffffff",
              position: "relative",
            }}
          >
            {loadingDetail && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  background: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <CircularProgress sx={{ color: "#5b8def" }} size={32} />
              </Box>
            )}

            {lat && lon ? (
              <CommonMap
                routePath={routePath}
                center={{ lat: Number(lat), lng: Number(lon) }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  background:
                    "linear-gradient(145deg, #fafbfc 0%, #f2f4f7 100%)",
                }}
              >
                <LocationOnIcon sx={{ fontSize: 32, color: "#aab3c2" }} />
                <Typography sx={{ color: "#8b95a6", fontSize: "12px" }}>
                  {loadingDetail
                    ? "Loading location…"
                    : "Location unavailable"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Detail panel */}
          <Box
            sx={{
              flexShrink: 0,
              background: "#ffffff",
              borderTop: "1px solid rgba(15,23,42,0.06)",
              p: "16px 18px",
            }}
          >
            {loadingDetail && !detail ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: "#5b8def" }} />
                <Typography sx={{ color: "#5b8def", fontSize: "12px" }}>
                  Loading event details…
                </Typography>
              </Box>
            ) : detailError ? (
              <Typography sx={{ color: "#dc2626", fontSize: "12px" }}>
                ⚠ {detailError}
              </Typography>
            ) : (
              <>
                {/* Title row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: "12px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      {toDisplayType(activeRecord?.alarmType)}
                    </Typography>
                    {detail?.speedConvert && (
                      <Chip
                        icon={
                          <SpeedIcon sx={{ fontSize: "12px !important" }} />
                        }
                        label={detail.speedConvert}
                        size="small"
                        sx={{
                          height: 21,
                          fontSize: "11px",
                          fontWeight: 600,
                          background: "rgba(91,141,239,0.1)",
                          color: "#1e3a8a",
                          border: "1px solid rgba(30,58,138,0.1)",
                          "& .MuiChip-icon": { color: "#1e3a8a" },
                        }}
                      />
                    )}
                  </Box>

                  {/* Valid / Invalid toggle */}
                  <Box sx={{ display: "flex", gap: "8px" }}>
                    <Button
                      size="small"
                      startIcon={
                        <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
                      }
                      sx={{
                        height: 28,
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "none",
                        background:
                          detail?.invalidStatusValue === "Valid"
                            ? "#10b981"
                            : "rgba(16,185,129,0.09)",
                        color:
                          detail?.invalidStatusValue === "Valid"
                            ? "#ffffff"
                            : "#0a7a5a",
                        border:
                          detail?.invalidStatusValue === "Valid"
                            ? "none"
                            : "1px solid rgba(10,122,90,0.12)",
                        borderRadius: "7px",
                        px: 1.5,
                        boxShadow:
                          detail?.invalidStatusValue === "Valid"
                            ? "0 2px 6px rgba(16,185,129,0.3)"
                            : "none",
                        "&:hover": { background: "#0ea271" },
                      }}
                    >
                      Valid
                    </Button>
                    <Button
                      size="small"
                      startIcon={
                        <CancelIcon sx={{ fontSize: "14px !important" }} />
                      }
                      sx={{
                        height: 28,
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "none",
                        background:
                          detail?.invalidStatusValue === "Invalid"
                            ? "#ef4444"
                            : "rgba(239,68,68,0.09)",
                        color:
                          detail?.invalidStatusValue === "Invalid"
                            ? "#ffffff"
                            : "#a32424",
                        border:
                          detail?.invalidStatusValue === "Invalid"
                            ? "none"
                            : "1px solid rgba(163,36,36,0.12)",
                        borderRadius: "7px",
                        px: 1.5,
                        boxShadow:
                          detail?.invalidStatusValue === "Invalid"
                            ? "0 2px 6px rgba(239,68,68,0.3)"
                            : "none",
                        "&:hover": { background: "#e23b3b" },
                      }}
                    >
                      Invalid
                    </Button>
                  </Box>
                </Box>

                {/* Info grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px 20px",
                    background: "#fafbfc",
                    border: "1px solid rgba(15,23,42,0.05)",
                    borderRadius: "10px",
                    p: "12px 14px",
                  }}
                >
                  <InfoRow
                    icon={<DevicesIcon />}
                    label="Device Name"
                    value={
                      detail?.deviceName ?? activeRecord?.deviceName ?? "—"
                    }
                  />
                  <InfoRow
                    icon={<FingerprintIcon />}
                    label="IMEI"
                    value={detail?.deviceId ?? "—"}
                  />
                  <InfoRow
                    icon={<AccessTimeIcon />}
                    label="Alert Time"
                    value={detail?.alarmTs ?? activeRecord?.alarmTime ?? "—"}
                  />
                  <InfoRow
                    icon={<MyLocationIcon />}
                    label="Coordinate Points"
                    value={
                      lat && lon
                        ? `${Number(lon).toFixed(6)} / ${Number(lat).toFixed(6)}`
                        : "—"
                    }
                  />
                  <InfoRow
                    icon={<LocationOnIcon />}
                    label="Address"
                    value={address}
                    wide
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

// ─── Tiny info row ────────────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value, wide }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: "7px",
      gridColumn: wide ? "1 / -1" : undefined,
    }}
  >
    <Box
      sx={{
        color: "#5b8def",
        mt: "2px",
        flexShrink: 0,
        "& svg": { fontSize: 13 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="span"
        sx={{ fontSize: "11px", color: "#8b95a6", mr: "5px" }}
      >
        {label}:
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: "12px",
          color: "#1f2937",
          fontWeight: 600,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

export default EventDetailModal;